import { supabase } from '../lib/supabase';
import { emailService } from './emailService';
import toastService from '../components/ui/Toast';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const disputeService = {
  // Get disputes for a specific charge
  async getDisputesByCharge(chargeId) {
    try {
      const { data, error } = await supabase
        ?.from('dispute_responses')
        ?.select(`
          *,
          responder:user_profiles!dispute_responses_responder_id_fkey(id, full_name, email, role)
        `)
        ?.eq('dispute_id', chargeId)
        ?.order('created_at', { ascending: true });

      if (error) {
        console.error('Get disputes error:', error);
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw new Error(error?.message || 'Failed to fetch disputes');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get disputes by charge error:', error);
      throw error;
    }
  },

  // Get all disputes for owner (disputes on their charges)
  async getOwnerDisputes(filters = {}) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // First get charges for this owner
      let chargesQuery = supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          booking:bookings!rental_violation_charges_booking_id_fkey(
            id,
            booking_reference,
            start_date,
            end_date,
            trailer_id
          ),
          renter:user_profiles!rental_violation_charges_renter_id_fkey(id, full_name, email)
        `)
        ?.eq('owner_id', user?.id)
        ?.eq('charge_status', 'disputed');

      const { data: charges, error: chargesError } = await chargesQuery?.order('created_at', { ascending: false });

      if (chargesError) {
        console.error('Get owner charges error:', chargesError);
        if (chargesError?.code === 'PGRST116') {
          return [];
        }
        throw new Error(chargesError?.message || 'Failed to fetch disputes');
      }

      if (!charges || charges?.length === 0) {
        return [];
      }

      // Get dispute responses for these charges
      const chargeIds = charges?.map(c => c?.id);
      const { data: responses, error: responsesError } = await supabase
        ?.from('dispute_responses')
        ?.select(`
          *,
          responder:user_profiles!dispute_responses_responder_id_fkey(id, full_name, email, role)
        `)
        ?.in('dispute_id', chargeIds)
        ?.order('created_at', { ascending: false });

      if (responsesError) {
        console.error('Get responses error:', responsesError);
      }

      // Combine charges with their responses
      const disputes = charges?.map(charge => ({
        ...toCamelCase(charge),
        responses: responses?.filter(r => r?.dispute_id === charge?.id)?.map(toCamelCase) || []
      }));

      return disputes;
    } catch (error) {
      console.error('Get owner disputes error:', error);
      throw error;
    }
  },

  // Get all disputes for renter (disputes they filed)
  async getRenterDisputes() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get disputed charges for this renter
      const { data: charges, error: chargesError } = await supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          booking:bookings!rental_violation_charges_booking_id_fkey(
            id,
            booking_reference,
            start_date,
            end_date,
            trailer_id
          ),
          owner:user_profiles!rental_violation_charges_owner_id_fkey(id, full_name, email)
        `)
        ?.eq('renter_id', user?.id)
        ?.eq('charge_status', 'disputed')
        ?.order('created_at', { ascending: false });

      if (chargesError) {
        console.error('Get renter charges error:', chargesError);
        if (chargesError?.code === 'PGRST116') {
          return [];
        }
        throw new Error(chargesError?.message || 'Failed to fetch disputes');
      }

      if (!charges || charges?.length === 0) {
        return [];
      }

      // Get dispute responses
      const chargeIds = charges?.map(c => c?.id);
      const { data: responses, error: responsesError } = await supabase
        ?.from('dispute_responses')
        ?.select(`
          *,
          responder:user_profiles!dispute_responses_responder_id_fkey(id, full_name, email, role)
        `)
        ?.in('dispute_id', chargeIds)
        ?.order('created_at', { ascending: false });

      if (responsesError) {
        console.error('Get responses error:', responsesError);
      }

      // Combine charges with responses
      const disputes = charges?.map(charge => ({
        ...toCamelCase(charge),
        responses: responses?.filter(r => r?.dispute_id === charge?.id)?.map(toCamelCase) || []
      }));

      return disputes;
    } catch (error) {
      console.error('Get renter disputes error:', error);
      throw error;
    }
  },

  // Create dispute response
  async createDisputeResponse(responseData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Upload counter-evidence if provided
      let counterEvidenceUrls = [];
      if (responseData?.counterEvidenceFiles && responseData?.counterEvidenceFiles?.length > 0) {
        counterEvidenceUrls = await this.uploadCounterEvidence(responseData?.counterEvidenceFiles);
      }

      const responsePayload = toSnakeCase({
        disputeId: responseData?.disputeId,
        responderId: user?.id,
        responderRole: responseData?.responderRole,
        responseType: responseData?.responseType,
        responseMessage: responseData?.responseMessage,
        settlementAmount: responseData?.settlementAmount,
        rejectionReason: responseData?.rejectionReason,
        counterEvidenceUrls: counterEvidenceUrls
      });

      const { data, error } = await supabase
        ?.from('dispute_responses')
        ?.insert(responsePayload)
        ?.select(`
          *,
          responder:user_profiles!dispute_responses_responder_id_fkey(id, full_name, email, role)
        `)
        ?.single();

      if (error) {
        console.error('Create dispute response error:', error);
        throw new Error(error?.message || 'Failed to create dispute response');
      }

      // Send email notification
      await this.sendDisputeNotification(responseData?.disputeId, 'response_added');

      return toCamelCase(data);
    } catch (error) {
      console.error('Create dispute response error:', error);
      throw error;
    }
  },

  // File a dispute response
  async fileDisputeResponse(disputeId, responseData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const snakeCaseData = toSnakeCase(responseData);
      snakeCaseData.responder_id = user?.id;
      snakeCaseData.dispute_id = disputeId;

      const { data, error } = await supabase
        ?.from('dispute_responses')
        ?.insert(snakeCaseData)
        ?.select(`
          *,
          responder:user_profiles!dispute_responses_responder_id_fkey(id, full_name, email, role)
        `)
        ?.single();

      if (error) {
        console.error('File dispute response error:', error);
        throw new Error(error?.message || 'Failed to file dispute response');
      }

      // Show toast notification
      toastService?.success('Dispute response submitted successfully');

      return toCamelCase(data);
    } catch (error) {
      console.error('File dispute response error:', error);
      throw error;
    }
  },

  // Upload counter-evidence files
  async uploadCounterEvidence(files) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const uploadPromises = files?.map(async (file) => {
        const fileExt = file?.name?.split('.')?.pop();
        const fileName = `${user?.id}/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;

        const { data, error } = await supabase?.storage
          ?.from('dispute-counter-evidence')
          ?.upload(fileName, file);

        if (error) {
          console.error('File upload error:', error);
          throw new Error(`Failed to upload ${file?.name}`);
        }

        // Get signed URL (valid for 1 year)
        const { data: urlData, error: urlError } = await supabase?.storage
          ?.from('dispute-counter-evidence')
          ?.createSignedUrl(fileName, 31536000);

        if (urlError) {
          console.error('Signed URL error:', urlError);
          throw new Error('Failed to generate file URL');
        }

        return urlData?.signedUrl;
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Upload counter-evidence error:', error);
      throw error;
    }
  },

  // Send dispute notification email
  async sendDisputeNotification(chargeId, notificationType) {
    try {
      // Get charge details
      const { data: charge, error: chargeError } = await supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          booking:bookings!rental_violation_charges_booking_id_fkey(
            id,
            booking_reference,
            start_date,
            end_date
          ),
          owner:user_profiles!rental_violation_charges_owner_id_fkey(id, full_name, email),
          renter:user_profiles!rental_violation_charges_renter_id_fkey(id, full_name, email)
        `)
        ?.eq('id', chargeId)
        ?.single();

      if (chargeError || !charge) {
        console.error('Failed to fetch charge for notification:', chargeError);
        return;
      }

      // Determine recipient based on notification type
      let recipientEmail, recipientName;
      if (notificationType === 'dispute_filed') {
        // Notify owner when renter files dispute
        recipientEmail = charge?.owner?.email;
        recipientName = charge?.owner?.full_name;
      } else if (notificationType === 'response_added') {
        // Notify renter when owner responds
        recipientEmail = charge?.renter?.email;
        recipientName = charge?.renter?.full_name;
      }

      if (!recipientEmail) {
        console.error('No recipient email found for notification');
        return;
      }

      // Send email
      await emailService?.sendDisputeUpdate({
        recipientEmail,
        recipientName,
        bookingData: {
          bookingReference: charge?.booking?.booking_reference,
          disputeStatus: charge?.charge_status,
          chargeAmount: charge?.charge_amount
        },
        trailerData: {
          title: 'Trailer Rental'
        }
      });
    } catch (error) {
      console.error('Send dispute notification error:', error);
      // Don't throw - email failure shouldn't break the flow
    }
  },

  // Subscribe to dispute updates
  subscribeToDisputes(userId, callback) {
    const channel = supabase
      ?.channel('dispute_updates')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dispute_responses'
        },
        (payload) => {
          callback?.(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rental_violation_charges',
          filter: `charge_status=eq.disputed`
        },
        (payload) => {
          callback?.(payload);
        }
      )
      ?.subscribe();

    return channel;
  },

  // Get all flagged disputes for admin moderation
  async getAdminDisputes(filters = {}) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Fetch all disputed charges
      let query = supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          booking:bookings!rental_violation_charges_booking_id_fkey(
            id,
            booking_reference,
            start_date,
            end_date,
            trailer_listing_id
          ),
          owner:user_profiles!rental_violation_charges_owner_id_fkey(id, full_name, email, role),
          renter:user_profiles!rental_violation_charges_renter_id_fkey(id, full_name, email, role),
          trailer:trailer_listings(id, title, trailer_type)
        `)
        ?.eq('charge_status', 'disputed');

      // Apply filters
      if (filters?.status) {
        // Status filter handled by charge_status above
      }

      const { data: charges, error: chargesError } = await query?.order('dispute_date', { ascending: false });

      if (chargesError) {
        console.error('Get admin disputes error:', chargesError);
        if (chargesError?.code === 'PGRST116') {
          return [];
        }
        throw new Error(chargesError?.message || 'Failed to fetch disputes');
      }

      if (!charges || charges?.length === 0) {
        return [];
      }

      // Get dispute responses for these charges
      const chargeIds = charges?.map(c => c?.id);
      const { data: responses, error: responsesError } = await supabase
        ?.from('dispute_responses')
        ?.select(`
          *,
          responder:user_profiles!dispute_responses_responder_id_fkey(id, full_name, email, role)
        `)
        ?.in('dispute_id', chargeIds)
        ?.order('created_at', { ascending: true });

      if (responsesError) {
        console.error('Get responses error:', responsesError);
      }

      // Combine charges with their responses
      const disputes = charges?.map(charge => ({
        ...toCamelCase(charge),
        responses: responses?.filter(r => r?.dispute_id === charge?.id)?.map(toCamelCase) || []
      }));

      return disputes;
    } catch (error) {
      console.error('Get admin disputes error:', error);
      throw error;
    }
  },

  // Admin: Apply settlement decision
  async applySettlementDecision(disputeId, decision) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const snakeCaseDecision = toSnakeCase(decision);

      // Create admin response
      const { data: response, error: responseError } = await supabase
        ?.from('dispute_responses')
        ?.insert({
          dispute_id: disputeId,
          responder_id: user?.id,
          responder_role: 'admin',
          response_type: snakeCaseDecision?.responseType || 'partial_settlement',
          response_message: snakeCaseDecision?.responseMessage,
          settlement_amount: snakeCaseDecision?.settlementAmount,
          admin_decision: snakeCaseDecision?.adminDecision,
          settlement_notes: snakeCaseDecision?.settlementNotes,
          resolved_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (responseError) {
        console.error('Apply settlement error:', responseError);
        throw new Error(responseError?.message || 'Failed to apply settlement');
      }

      // Update charge status based on decision
      let newStatus = 'disputed';
      if (decision?.adminDecision === 'accept_owner') {
        newStatus = 'paid';
      } else if (decision?.adminDecision === 'accept_renter') {
        newStatus = 'cancelled';
      } else if (decision?.adminDecision === 'partial_settlement') {
        newStatus = 'paid';
      }

      const { error: updateError } = await supabase
        ?.from('rental_violation_charges')
        ?.update({ charge_status: newStatus })
        ?.eq('id', disputeId);

      if (updateError) {
        console.error('Update charge status error:', updateError);
      }

      // Show toast notification
      toastService?.success('Settlement decision applied successfully');

      // Send notification emails
      const { data: charge } = await supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          owner:user_profiles!rental_violation_charges_owner_id_fkey(full_name, email),
          renter:user_profiles!rental_violation_charges_renter_id_fkey(full_name, email),
          booking:bookings(booking_reference)
        `)
        ?.eq('id', disputeId)
        ?.single();

      if (charge) {
        // Notify owner
        await this?.sendDisputeNotification(
          charge?.owner?.email,
          charge?.owner?.full_name,
          {
            bookingReference: charge?.booking?.booking_reference,
            chargeAmount: charge?.charge_amount,
            status: newStatus,
            adminDecision: decision?.adminDecision,
            settlementNotes: decision?.settlementNotes
          }
        );

        // Notify renter
        await this?.sendDisputeNotification(
          charge?.renter?.email,
          charge?.renter?.full_name,
          {
            bookingReference: charge?.booking?.booking_reference,
            chargeAmount: charge?.charge_amount,
            status: newStatus,
            adminDecision: decision?.adminDecision,
            settlementNotes: decision?.settlementNotes
          }
        );
      }

      return toCamelCase(response);
    } catch (error) {
      console.error('Apply settlement decision error:', error);
      throw error;
    }
  },

  // Subscribe to admin dispute updates
  subscribeToAdminDisputes(callback) {
    const channel = supabase
      ?.channel('admin_disputes_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rental_violation_charges',
          filter: 'charge_status=eq.disputed'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return channel;
  }
};
